/**
 * Raiz do formulário público de extensão de plano (RN 561).
 *
 * Responsabilidades desta camada, e só delas:
 *   1. ler o token da URL e carregar o `resolve`;
 *   2. semear o estado do wizard com o que veio do backend (spec §7 e §10.6);
 *   3. guardar o passo corrente e o desfecho, expondo tudo por contexto;
 *   4. mostrar 404 neutra quando o token não serve (spec §3).
 *
 * O estado do wizard é criado por `useState` com inicializador preguiçoso a
 * partir do `resolve` — por isso o wizard só monta com os dados em mãos, e não
 * existe efeito sincronizando resposta e estado depois da primeira renderização.
 *
 * Sem salvamento parcial (spec §1): um refresh recomeça no Step 0.
 */

import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ComponentType, ReactNode, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { resolveExtensionForm } from "./api";
import { ExtensionStepper } from "./components/ExtensionStepper";
import { PoweredByFooter } from "./components/PoweredByFooter";
import {
  clampStep,
  createSeededState,
  ExtensionContext,
  IExtensionContextValue,
} from "./context";
import { getPreviewResolve, isPreviewToken } from "./devPreview";
import { OutcomeScreen } from "./outcomes/OutcomeScreen";
import { Step0Login } from "./steps/Step0Login";
import { Step1Holder } from "./steps/Step1Holder";
import { Step2Plan } from "./steps/Step2Plan";
import { Step3Dependents } from "./steps/Step3Dependents";
import { Step4Documents } from "./steps/Step4Documents";
import { Step5Review } from "./steps/Step5Review";
import { Background, Card, Paragraph, Stack, Title } from "./styled";
import {
  ExtensionOutcome,
  ExtensionStep,
  IExtensionFormState,
  IResolveResponse,
} from "./types";

const STEP_COMPONENTS: Record<ExtensionStep, ComponentType> = {
  [ExtensionStep.Login]: Step0Login,
  [ExtensionStep.Holder]: Step1Holder,
  [ExtensionStep.Plan]: Step2Plan,
  [ExtensionStep.Dependents]: Step3Dependents,
  [ExtensionStep.Documents]: Step4Documents,
  [ExtensionStep.Review]: Step5Review,
};

/**
 * Casca visual comum a carregamento, erro e wizard.
 *
 * `narrow` reproduz a diferença de largura do Figma: o Step 0, as telas finais e
 * os estados de carregamento/erro são cards estreitos e centrados; só os passos
 * 1 a 5 ocupam a largura cheia, porque precisam dela para as duas colunas e para
 * os três cards de plano lado a lado.
 */
function Shell({
  children,
  narrow = false,
}: {
  children: ReactNode;
  narrow?: boolean;
}) {
  return (
    <Background>
      <Card $narrow={narrow}>{children}</Card>
    </Background>
  );
}

function LoadingScreen() {
  return (
    <Shell narrow>
      <Stack
        $gap="16px"
        style={{ alignItems: "center", padding: "64px 0" }}
      >
        <CircularProgress />
        <Paragraph>Carregando sua oferta...</Paragraph>
      </Stack>
    </Shell>
  );
}

/**
 * 404 neutra. O spec §3 proíbe distinguir "token nunca existiu" de "token
 * expirado" ou "já respondido": qualquer falha no `resolve` mostra o mesmo
 * texto, para o link não virar oráculo sobre quem recebeu a oferta.
 */
function InvalidTokenScreen() {
  return (
    <Shell narrow>
      <Stack $gap="8px">
        <Title>Link indisponível</Title>
        <Paragraph>
          Este link não está mais disponível. Ele pode ter expirado ou já ter
          sido utilizado.
        </Paragraph>
        <Paragraph>
          Se você acredita que isso é um engano, procure o RH da sua empresa.
        </Paragraph>
      </Stack>
      <PoweredByFooter />
    </Shell>
  );
}

interface IWizardProps {
  organizationId: string;
  token: string;
  data: IResolveResponse;
}

/**
 * Wizard propriamente dito. Só monta com o `resolve` já concluído, o que deixa
 * `data` estável e não-nulo para todos os steps.
 */
function ExtensionWizard({ organizationId, token, data }: IWizardProps) {
  const [state, setState] = useState<IExtensionFormState>(() =>
    createSeededState(data)
  );
  const [currentStep, setCurrentStep] = useState<ExtensionStep>(
    ExtensionStep.Login
  );
  const [outcome, setOutcome] = useState<ExtensionOutcome | null>(null);

  const updateState = useCallback((patch: Partial<IExtensionFormState>) => {
    setState((current) => ({ ...current, ...patch }));
  }, []);

  /**
   * Cada troca de passo volta ao topo: no mobile os steps são mais altos que a
   * viewport e, sem isso, o titular cairia no meio da tela seguinte.
   */
  const goToStep = useCallback((step: ExtensionStep) => {
    setCurrentStep(clampStep(step));
    window.scrollTo({ top: 0 });
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((current) => clampStep(current + 1));
    window.scrollTo({ top: 0 });
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((current) => clampStep(current - 1));
    window.scrollTo({ top: 0 });
  }, []);

  const value = useMemo<IExtensionContextValue>(
    () => ({
      organizationId,
      token,
      data,
      state,
      currentStep,
      updateState,
      goToStep,
      goNext,
      goBack,
      setOutcome,
    }),
    [
      organizationId,
      token,
      data,
      state,
      currentStep,
      updateState,
      goToStep,
      goNext,
      goBack,
    ]
  );

  if (outcome) {
    return (
      <Shell narrow>
        <OutcomeScreen outcome={outcome} />
      </Shell>
    );
  }

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <ExtensionContext.Provider value={value}>
      <Shell narrow={currentStep === ExtensionStep.Login}>
        <ExtensionStepper currentStep={currentStep} />
        <StepComponent />
        <PoweredByFooter />
      </Shell>
    </ExtensionContext.Provider>
  );
}

export function PlanExtensionForm() {
  const { organizationId, token } = useParams<{
    organizationId: string;
    token: string;
  }>();

  const { data, isPending, isError } = useQuery({
    queryKey: ["plan-extension-form", organizationId, token],
    queryFn: () =>
      isPreviewToken(token)
        ? getPreviewResolve()
        : resolveExtensionForm(organizationId, token),
    /**
     * Token inválido/expirado é resposta definitiva do backend, não instabilidade
     * de rede: repetir só atrasaria a 404 neutra.
     */
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (isPending) return <LoadingScreen />;

  if (isError || !data) return <InvalidTokenScreen />;

  return (
    <ExtensionWizard
      organizationId={organizationId}
      token={token}
      data={data}
    />
  );
}
